import { useMutation, useQueryClient } from '@tanstack/react-query'
import { v2SelectStartupOutreachChannelsMutation } from '@workspace/api-backend/react-query'
import { outreachStatusQueryKey } from '../../outreach/api'
import { useInvalidateHomeState } from './use-home-state'

export type CampaignChannel = 'investor' | 'warm_intro'

/**
 * Switch a campaign on or off from the Home card.
 *
 * The server refuses to leave a founder with no channel at all, so the last one
 * standing is not offered as a toggle rather than being offered and rejected —
 * a control that exists only to fail is worse than one that is not there.
 *
 * Both channels are sent on every call because the endpoint takes the whole
 * selection, not a delta.
 */
export function useCampaignToggle(
  startupId: string,
  current: { investor: boolean; warmIntro: boolean }
) {
  const queryClient = useQueryClient()
  const invalidateHome = useInvalidateHomeState(startupId)
  const mutation = useMutation(v2SelectStartupOutreachChannelsMutation())

  const set = (next: { investor: boolean; warmIntro: boolean }) =>
    mutation.mutate(
      { path: { id: startupId }, body: { investor: next.investor, portfolio: next.warmIntro } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: outreachStatusQueryKey(startupId) })
          await invalidateHome()
        },
      }
    )

  return {
    isPending: mutation.isPending,
    /** Whether turning this one off would leave the founder with nothing on. */
    isLastEnabled: (channel: CampaignChannel) =>
      channel === 'investor'
        ? current.investor && !current.warmIntro
        : current.warmIntro && !current.investor,
    toggle: (channel: CampaignChannel) =>
      set(
        channel === 'investor'
          ? { investor: !current.investor, warmIntro: current.warmIntro }
          : { investor: current.investor, warmIntro: !current.warmIntro }
      ),
  }
}
